/**
 * Day 2, the business sources: each owned source with its latest
 * acquisition, the add-a-source form, and the evidence acquisition action
 * while the run is still at intake or evidence.
 */

import type { ReactNode } from 'react';

import {
  acquireAnalysisEvidence,
  addAnalysisSources,
  newIdempotencyKey,
  type AnalysisDay2State,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card, selectStyle } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { SourceForm } from './useSourceForm.js';

export function BusinessSourcesSection({
  id,
  day2,
  mayAcquire,
  busy,
  mutate,
  form,
  copy,
}: {
  readonly id: string;
  readonly day2: AnalysisDay2State;
  readonly mayAcquire: boolean;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly form: SourceForm;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  const {
    newPlatform,
    setNewPlatform,
    newUrl,
    setNewUrl,
    newIdentity,
    setNewIdentity,
  } = form;

  return (
    <section id="business-sources" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>{copy.sources}</SectionHeading>
      <div style={{ display: 'grid', gap: 10 }}>
        {day2.business.sources.map((source) => {
          const acquisition = [...day2.acquisitions]
            .reverse()
            .find((entry) => entry.sourceId === source.id);
          return (
            <article key={source.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{source.platform}</strong>
                <Tag tone={toneFor(acquisition?.resultState ?? 'PENDING')}>
                  {acquisition?.resultState ?? 'PENDING'}
                </Tag>
              </div>
              <a href={source.canonicalUrl} target="_blank" rel="noreferrer">
                {source.canonicalUrl}
              </a>
              <p style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
                Identity: {source.externalIdentity ?? 'unknown'} ·{' '}
                {source.identityBasis}
              </p>
              {acquisition ? (
                <p style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
                  {acquisition.provider} · {acquisition.operation} ·{' '}
                  {acquisition.itemCount ?? 'unknown'} item(s)
                </p>
              ) : null}
              {acquisition?.errorDetail ? (
                <InlineAlert tone="error" title={acquisition.errorDetail} />
              ) : null}
            </article>
          );
        })}
      </div>
      {mayAcquire ? (
        <form
          style={{ ...card, marginTop: 12 }}
          onSubmit={(event) => {
            event.preventDefault();
            void mutate('source', async () => {
              await addAnalysisSources(id, newIdempotencyKey(), [
                {
                  platform: newPlatform,
                  canonicalUrl: newUrl,
                  ...(newIdentity.trim() ? { externalIdentity: newIdentity } : {}),
                },
              ]);
              setNewUrl('');
              setNewIdentity('');
            });
          }}
        >
          <div className="mj-form-two-column">
            <label>
              <span>Platform</span>
              <select
                value={newPlatform}
                onChange={(event) => {
                  setNewPlatform(event.target.value);
                }}
                style={selectStyle}
              >
                <option value="WEBSITE">Website</option>
                <option value="INSTAGRAM">Instagram</option>
                <option value="TIKTOK">TikTok</option>
              </select>
            </label>
            <TextField
              label="URL"
              value={newUrl}
              onChange={setNewUrl}
              type="url"
              dir="ltr"
            />
            <TextField
              label="Username / external identity (optional)"
              value={newIdentity}
              onChange={setNewIdentity}
              dir="ltr"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            busy={busy === 'source'}
            disabled={!newUrl.trim()}
          >
            {copy.addSource}
          </Button>
        </form>
      ) : null}
      {mayAcquire ? (
        <Button
          variant="primary"
          busy={busy === 'acquire'}
          onClick={() =>
            void mutate('acquire', () =>
              acquireAnalysisEvidence(id, newIdempotencyKey()),
            )
          }
          style={{ marginTop: 12 }}
        >
          {copy.acquire}
        </Button>
      ) : null}
    </section>
  );
}
